// ROOM 404: Persistent In-App Notifications Modal
// Mobile-first, optimistic UI, accessible keyboard navigation, offline resilience

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Award,
  Bell,
  Calendar,
  Check,
  CheckCheck,
  Megaphone,
  RefreshCw,
  Shield,
  ShieldCheck,
  Sparkles,
  Target,
  UserCheck,
  WifiOff,
  X,
} from 'lucide-react';
import { apiRequest } from '../services/api';
import type { NotificationItem } from '../types';

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
  onOpenPushSettings?: () => void;
  onOpenInstallGuide?: () => void;
}

export const NotificationsModal: React.FC<NotificationsModalProps> = ({
  isOpen,
  onClose,
  onUnreadCountChange,
  onOpenPushSettings,
  onOpenInstallGuide,
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);

  const modalRef = useRef<HTMLDivElement>(null);

  // Monitor network offline / online status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch notifications from live API
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiRequest<{
        notifications: NotificationItem[];
        unreadCount: number;
        hasMore: boolean;
      }>('/api/notifications?limit=30');

      setNotifications(res.notifications);
      setUnreadCount(res.unreadCount);
      onUnreadCountChange?.(res.unreadCount);
    } catch (err: any) {
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  // Load when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
      setActionError(null);
    }
  }, [isOpen, fetchNotifications]);

  // Keyboard accessibility: Escape key closes modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Mark single notification as read (Optimistic UI with Rollback)
  const handleMarkOneRead = async (notificationId: string) => {
    const target = notifications.find((n) => n.id === notificationId);
    if (!target || target.isRead) return;

    // Snapshot previous state for rollback
    const prevNotifications = [...notifications];
    const prevUnreadCount = unreadCount;

    // Optimistic state transition
    const nextUnread = Math.max(0, unreadCount - 1);
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n))
    );
    setUnreadCount(nextUnread);
    onUnreadCountChange?.(nextUnread);
    setActionError(null);

    try {
      const res = await apiRequest<{
        success: boolean;
        notificationId: string;
        isRead: boolean;
        unreadCount: number;
      }>(`/api/notifications/${notificationId}/read`, {
        method: 'POST',
      });
      // Synchronize authoritative unread count
      if (typeof res.unreadCount === 'number') {
        setUnreadCount(res.unreadCount);
        onUnreadCountChange?.(res.unreadCount);
      }
    } catch (err: any) {
      // Rollback on network failure
      setNotifications(prevNotifications);
      setUnreadCount(prevUnreadCount);
      onUnreadCountChange?.(prevUnreadCount);
      setActionError('Could not mark notification as read. Please try again.');
    }
  };

  // Mark all notifications as read (Optimistic UI with Rollback)
  const handleMarkAllRead = async () => {
    if (unreadCount === 0) return;

    // Snapshot previous state
    const prevNotifications = [...notifications];
    const prevUnreadCount = unreadCount;

    // Optimistic transition
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    onUnreadCountChange?.(0);
    setActionError(null);

    try {
      await apiRequest<{ success: boolean; updatedCount: number; unreadCount: number }>(
        '/api/notifications/read-all',
        { method: 'POST' }
      );
    } catch (err: any) {
      // Rollback on network failure
      setNotifications(prevNotifications);
      setUnreadCount(prevUnreadCount);
      onUnreadCountChange?.(prevUnreadCount);
      setActionError('Failed to mark all as read. Changes reverted.');
    }
  };

  if (!isOpen) return null;

  const filteredNotifications =
    filter === 'unread' ? notifications.filter((n) => !n.isRead) : notifications;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'verification':
        return <ShieldCheck className="w-4 h-4 text-emerald-400" />;
      case 'mission':
        return <Target className="w-4 h-4 text-blue-400" />;
      case 'event':
        return <Calendar className="w-4 h-4 text-purple-400" />;
      case 'report':
        return <AlertCircle className="w-4 h-4 text-amber-400" />;
      case 'standing':
        return <Shield className="w-4 h-4 text-rose-400" />;
      case 'role':
        return <Award className="w-4 h-4 text-yellow-400" />;
      case 'announcement':
        return <Megaphone className="w-4 h-4 text-cyan-400" />;
      default:
        return <Bell className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatTimestamp = (ts: number) => {
    const now = Date.now();
    const diffMs = now - ts;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-sm animate-fade-in"
      role="dialog"
      aria-modal="true"
      aria-label="Notifications"
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-md bg-[#12141c] border border-[#1f2433] rounded-2xl p-4 sm:p-5 shadow-2xl max-h-[88vh] flex flex-col text-gray-100"
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-[#1f2433]">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <Bell className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-bold text-white tracking-tight flex items-center space-x-2">
                <span>Notifications</span>
                {unreadCount > 0 && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                    {unreadCount} new
                  </span>
                )}
              </h2>
            </div>
          </div>

          <div className="flex items-center space-x-1">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-gray-400 hover:text-emerald-400 px-2 py-1 rounded hover:bg-[#1a1e2d] transition-colors flex items-center space-x-1"
                title="Mark all as read"
                aria-label="Mark all notifications as read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Mark all</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-[#1a1e2d] transition-colors"
              aria-label="Close notifications modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filter Chips & Offline Banner */}
        <div className="pt-2.5 pb-2 flex items-center justify-between">
          <div className="flex items-center space-x-1.5 text-xs">
            <button
              onClick={() => setFilter('all')}
              className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-[#181b26]'
              }`}
            >
              All ({notifications.length})
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${
                filter === 'unread'
                  ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-[#181b26]'
              }`}
            >
              Unread ({unreadCount})
            </button>
          </div>

          <button
            onClick={fetchNotifications}
            disabled={loading}
            className="text-gray-400 hover:text-white p-1 rounded hover:bg-[#181b26] transition-colors"
            title="Refresh feed"
            aria-label="Refresh notifications"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-emerald-400' : ''}`} />
          </button>
        </div>

        {/* Web Push & Install Quick Bar */}
        <div className="mb-2.5 p-2 rounded-xl bg-[#090a0f] border border-[#1f2433] flex items-center justify-between text-xs">
          <div className="flex items-center space-x-1.5 text-gray-300 text-[11px]">
            <Bell className="w-3.5 h-3.5 text-emerald-400" />
            <span>Emergency Web Push Alerts</span>
          </div>
          <div className="flex items-center space-x-1.5">
            {onOpenInstallGuide && (
              <button
                type="button"
                onClick={onOpenInstallGuide}
                className="text-[10px] text-gray-400 hover:text-cyan-400 px-2 py-0.5 rounded hover:bg-[#181b26] transition-colors"
              >
                App Guide
              </button>
            )}
            {onOpenPushSettings && (
              <button
                type="button"
                onClick={onOpenPushSettings}
                className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 transition-colors"
              >
                Configure
              </button>
            )}
          </div>
        </div>

        {/* Action Error Banner */}
        {actionError && (
          <div className="mb-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center space-x-2 animate-fade-in">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1">{actionError}</span>
            <button onClick={() => setActionError(null)} className="text-red-400 hover:text-white">
              <X className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Offline Banner */}
        {isOffline && (
          <div className="mb-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs flex items-center space-x-2">
            <WifiOff className="w-3.5 h-3.5 flex-shrink-0" />
            <span>You are offline. Showing cached notification feed.</span>
          </div>
        )}

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto space-y-2 mt-1 pr-0.5" tabIndex={0}>
          {loading && notifications.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center space-y-3">
              <div className="w-7 h-7 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs font-mono text-gray-400">Loading notifications...</span>
            </div>
          ) : error ? (
            <div className="py-12 text-center space-y-3">
              <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto" />
              <p className="text-xs text-gray-400">{error}</p>
              <button
                onClick={fetchNotifications}
                className="text-xs px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="py-14 text-center space-y-2">
              <div className="w-10 h-10 rounded-full bg-[#181b26] border border-[#262b3d] flex items-center justify-center mx-auto text-gray-500">
                <Check className="w-5 h-5 text-emerald-400/60" />
              </div>
              <p className="text-xs font-medium text-gray-300">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
              </p>
              <p className="text-[11px] text-gray-500 max-w-xs mx-auto">
                {filter === 'unread'
                  ? 'You are all caught up! Switch to All to see past notifications.'
                  : 'Verification decisions, mission rewards, and event updates will appear here.'}
              </p>
            </div>
          ) : (
            filteredNotifications.map((n) => (
              <div
                key={n.id}
                onClick={() => handleMarkOneRead(n.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleMarkOneRead(n.id);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`${n.isRead ? 'Read' : 'Unread'} notification: ${n.title}`}
                className={`group relative p-3 rounded-xl border transition-all cursor-pointer ${
                  n.isRead
                    ? 'bg-[#12141c]/60 border-[#1f2433] hover:border-[#2a3144] opacity-80'
                    : 'bg-[#181c27] border-emerald-500/30 hover:border-emerald-500/50 shadow-sm'
                }`}
              >
                <div className="flex items-start space-x-2.5">
                  <div
                    className={`p-1.5 rounded-lg flex-shrink-0 mt-0.5 ${
                      n.isRead ? 'bg-[#1a1e2b] text-gray-400' : 'bg-emerald-500/10 text-emerald-400'
                    }`}
                  >
                    {getNotificationIcon(n.type)}
                  </div>

                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between">
                      <h3
                        className={`text-xs font-semibold truncate ${
                          n.isRead ? 'text-gray-300' : 'text-white'
                        }`}
                      >
                        {n.title}
                      </h3>
                      <span className="text-[10px] text-gray-500 font-mono flex-shrink-0 ml-2">
                        {formatTimestamp(n.createdAt)}
                      </span>
                    </div>

                    <p className="text-xs text-gray-400 leading-relaxed break-words">{n.body}</p>

                    <div className="flex items-center justify-between pt-1">
                      <span className="text-[9px] uppercase tracking-wider font-mono text-gray-500">
                        {n.type}
                      </span>
                      {!n.isRead && (
                        <span className="inline-flex items-center text-[10px] text-emerald-400 hover:text-emerald-300 font-mono">
                          <Check className="w-3 h-3 mr-0.5" />
                          Tap to read
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {!n.isRead && (
                  <span className="absolute top-2.5 right-2.5 w-1.5 h-1.5 rounded-full bg-emerald-400 ring-2 ring-[#181c27]" />
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
